// squash/src/utils/passkey.ts
import { startRegistration, startAuthentication } from '@simplewebauthn/browser';
import config from '../config';

/**
 * Controleert of de browser en het apparaat passkeys (biometrie) ondersteunen.
 */
export async function checkPasskeySupport(): Promise<boolean> {
  // Controleer of WebAuthn beschikbaar is
  if (!window.PublicKeyCredential) return false;
  
  try {
    // Controleer specifiek op platform authenticators (Windows Hello, TouchID, FaceID)
    return await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
  } catch (err) {
    console.error('Passkey support check error:', err);
    return false;
  }
}

/**
 * Registreert een nieuwe passkey voor de ingelogde gebruiker.
 */
export async function registerPasskey(email: string, token: string, deviceId?: string): Promise<{ success: boolean; credentialId?: string } | false> {
  try {
    // 1. Haal registratie opties op van de server
    const optionsRes = await fetch(`${config.apiUrl}/api/v1/passkey/register/begin`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      }
    });

    if (!optionsRes.ok) {
      throw new Error('Kon registratie opties niet ophalen');
    }

    const options = await optionsRes.json();

    // 2. Start de browser/OS biometrie dialoog
    const credential = await startRegistration({ optionsJSON: options });

    // 3. Stuur de credential terug naar de server voor validatie en opslag
    const finishRes = await fetch(`${config.apiUrl}/api/v1/passkey/register/finish`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({
        credential,
        deviceId
      })
    });

    const result = await finishRes.json();
    return {
      success: result.success === true,
      credentialId: credential.id
    };
  } catch (err: any) {
    console.error('Passkey registratie gefaald:', err);
    // Specifieke fouten afhandelen
    if (err.name === 'NotAllowedError') {
      console.warn('Gebruiker heeft biometrie geannuleerd');
    }
    return false;
  }
}

/**
 * Logt in met een bestaande passkey.
 * Retourneert:
 *  - { success: true, token, user, credentialId }  bij succes
 *  - { success: false, cancelled: true }            als gebruiker dialoog annuleerde
 *  - { success: false, error: string }              bij een technische fout
 */
export async function loginWithPasskey(
  email: string,
  deviceId?: string,
  existingOptions?: any
): Promise<{ success: true; token: string; user: any; credentialId: string } |
           { success: false; cancelled?: boolean; error?: string } | null> {
  try {
    let options = existingOptions;

    // 1. Haal authenticatie opties op van de server als ze nog niet meegegeven zijn
    if (!options) {
      const optionsRes = await fetch(`${config.apiUrl}/api/v1/passkey/auth/begin`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });

      if (!optionsRes.ok) {
        const errorData = await optionsRes.json();
        return { success: false, error: errorData.message || 'Geen passkeys gevonden' };
      }

      options = await optionsRes.json();
    }

    // 2. Start de browser biometrie login
    let assertion: any;
    try {
      assertion = await startAuthentication({ optionsJSON: options });
    } catch (browserErr: any) {
      if (browserErr.name === 'NotAllowedError') {
        // Gebruiker heeft geannuleerd — geen foutmelding nodig
        console.info('[PASSKEY] Gebruiker heeft dialoog gesloten/geannuleerd.');
        return { success: false, cancelled: true };
      }
      console.error('[PASSKEY] Browser WebAuthn fout:', browserErr);
      return { success: false, error: browserErr.message || 'Browser fout' };
    }

    // 3. Stuur de assertion terug naar de server voor token generatie
    const finishRes = await fetch(`${config.apiUrl}/api/v1/passkey/auth/finish`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, assertion, deviceId })
    });

    if (!finishRes.ok) {
      const errData = await finishRes.json().catch(() => ({}));
      console.error('[PASSKEY] Server verificatie mislukt:', errData);
      return { success: false, error: errData.error || 'Verificatie mislukt op server' };
    }

    const result = await finishRes.json();
    if (result.success && result.token) {
      return {
        success: true,
        token: result.token,
        user: result.user,
        credentialId: assertion.id
      };
    }

    return { success: false, error: result.error || 'Onbekende fout' };

  } catch (err: any) {
    console.error('[PASSKEY] Onverwachte fout bij login:', err);
    return { success: false, error: err.message || 'Onbekende fout' };
  }
}


/**
 * Haalt de passkey status op van de ingelogde gebruiker.
 */
export async function getPasskeyStatus(token: string): Promise<any> {
    try {
        const res = await fetch(`${config.apiUrl}/api/v1/passkey/status`, {
            headers: { 'Authorization': `Bearer ${token}` }
        });
        return await res.json();
    } catch (err) {
        return { success: false, hasPasskey: false };
    }
}
